import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const CompanyContext = createContext(null);

export const useCompany = () => {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within a CompanyProvider');
  return ctx;
};

export const CompanyProvider = ({ children }) => {
  const { user, setUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Bootstrap from localStorage and from /companies endpoint
  useEffect(() => {
    if (!user) {
      setCompanies([]);
      setActiveCompanyId(null);
      return;
    }
    const cached = localStorage.getItem('companies');
    if (cached) {
      try { setCompanies(JSON.parse(cached)); } catch (_) {}
    }
    setActiveCompanyId(user.active_company_id || null);
  }, [user]);

  const refreshCompanies = useCallback(async () => {
    try {
      setLoadingCompanies(true);
      const res = await api.get('/companies');
      setCompanies(res.data || []);
      localStorage.setItem('companies', JSON.stringify(res.data || []));
    } catch (e) {
      console.error('Failed to load companies', e);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const selectCompany = useCallback(async (companyId) => {
    const res = await api.post('/auth/select-company', { company_id: companyId });
    const { token, user: userData, companies: newCompanies } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('companies', JSON.stringify(newCompanies || []));
    setUser(userData);
    setCompanies(newCompanies || []);
    setActiveCompanyId(companyId);
    return userData;
  }, [setUser]);

  const activeCompany = companies.find(c => c.id === activeCompanyId) || null;

  return (
    <CompanyContext.Provider value={{
      companies,
      activeCompany,
      activeCompanyId,
      loadingCompanies,
      refreshCompanies,
      selectCompany,
    }}>
      {children}
    </CompanyContext.Provider>
  );
};
